import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  CheckCircle,
  Image,
  Folder,
  Dashboard,
  SortByAlpha,
} from "@mui/icons-material";

export const InstructionsDialog = React.memo(({ open, onClose }) => {
  const [showButton, setShowButton] = React.useState(false);

  React.useEffect(() => {
    let timer;
    if (open) {
      setShowButton(false);
      timer = setTimeout(() => {
        setShowButton(true);
      }, process.env.NODE_ENV === 'production' ? 12000 : 0);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      onBackdropClick={() => null}
    >
      <DialogTitle>ITS Image Selects</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <Typography paragraph>Here's how to make your selects:</Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <Folder />
              </ListItemIcon>
              <ListItemText
                primary="Browse Folders"
                secondary="Click on folders to expand/collapse and view images"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Image />
              </ListItemIcon>
              <ListItemText
                primary="Select Images"
                secondary="Click on any image to add or remove it from your selects"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle />
              </ListItemIcon>
              <ListItemText
                primary="Confirm Selection"
                secondary="Click 'Confirm Selects' when you're done, this will send your selection off to the production team to start retouching"
              />
            </ListItem>
            <Divider sx={{ marginTop: 3, marginBottom: 4 }} />
            <Typography paragraph>Important info:</Typography>
            <ListItem>
              <ListItemIcon>
                <Dashboard />
              </ListItemIcon>
              <ListItemText
                primary="Storyboard Selects"
                secondary="The storyboard will automatically build as you make your selects"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <SortByAlpha />
              </ListItemIcon>
              <ListItemText
                primary="Image Order"
                secondary="The images on the storyboard will be in the same order as the shot folders"
              />
            </ListItem>
          </List>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            if (showButton) {
              onClose();
            }
          }}
          variant="contained"
        >
          {showButton ? (
            "Got it"
          ) : (
            <>
              <CircularProgress size={20} color="inherit" />{" "}
              <span style={{ marginLeft: 20 }}>Loading...</span>
            </>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export const ResetSelectsDialog = React.memo(({ open, onClose, onReset }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableScrollLock
    >
      <DialogTitle>Confirm Reset</DialogTitle>
      <DialogContent>
        Are you sure you want to reset your selections?
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="outlined">
          Cancel
        </Button>
        <Button onClick={onReset} color="error" variant="contained">
          Reset
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export const ConfirmSelectsDialog = React.memo(
  ({ open, onClose, onConfirm, selectedCount, onLoadingChange }) => {
    const [loading, setLoading] = React.useState(false);
    const [confirmed, setConfirmed] = React.useState(false);

    React.useEffect(() => {
      if (open) {
        setLoading(false);
        setConfirmed(false);
      }
    }, [open]);

    React.useEffect(() => {
      if (onLoadingChange) {
        onLoadingChange(loading);
      }
    }, [loading, onLoadingChange]);

    React.useEffect(() => {
      const handleBeforeUnload = (e) => {
        if (loading) {
          e.preventDefault();
          e.returnValue = "";
        }
      };
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () =>
        window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [loading]);

    const handleConfirm = async () => {
      setLoading(true);
      await onConfirm();
      setLoading(false);
      setConfirmed(true);
    };

    const handleDialogClose = (event, reason) => {
      if (loading && (reason === "backdropClick" || reason === "escapeKeyDown"))
        return;
      if (!loading) onClose();
    };

    const handleOkay = () => {
      onClose();
    };

    return (
      <Dialog
        open={open}
        onClose={handleDialogClose}
        disableScrollLock
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 12px 48px rgba(0,0,0,0.15)",
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1.5rem", letterSpacing: "-0.02em" }}>
          Confirm Selects
        </DialogTitle>
        <DialogContent sx={{ width: 500 }}>
          <DialogContentText sx={{ fontSize: "1rem", lineHeight: 1.7 }}>
            {loading ? (
              <>
                Your selections are being processed and the email is being sent to post-production.
                <br />
                <br />
                <strong style={{ color: "#f44336" }}>Please do not close this page or window.</strong>
              </>
            ) : confirmed ? (
              <>
                Thank you! Your <strong>{selectedCount}</strong>{" "}
                {selectedCount === 1 ? " image" : " images"} have been
                confirmed.
                <br />
                Post-production will be automatically emailed with your
                selection, and retouching will begin shortly.
              </>
            ) : (
              <>
                You've selected <strong>{selectedCount}</strong>
                {selectedCount === 1 ? " image" : " images"} for retouching.
                Would you like to confirm these selections? Once confirmed, the
                retouching process will begin and any changes afterward may
                incur additional fees.
                <br />
                <br />
                <strong>Delivery Timeline:</strong> Confirmed selections will be
                supplied within 5 business days from confirmation time (images
                confirmed after 12:30pm require one additional business day).
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          {loading ? (
            <Button
              color="primary"
              variant="contained"
              disabled
              sx={{
                borderRadius: "6px",
                fontWeight: 600,
                textTransform: "none",
                px: 4,
              }}
            >
              Processing....
            </Button>
          ) : confirmed ? (
            <Button
              onClick={handleOkay}
              color="primary"
              variant="contained"
              sx={{
                borderRadius: "6px",
                fontWeight: 600,
                textTransform: "none",
                px: 4,
                boxShadow: "0 2px 8px rgba(25,118,210,0.25)",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(25,118,210,0.35)",
                },
              }}
            >
              Okay
            </Button>
          ) : (
            <>
              <Button
                onClick={handleDialogClose}
                color="primary"
                variant="outlined"
                sx={{
                  borderRadius: "6px",
                  fontWeight: 600,
                  textTransform: "none",
                  px: 3,
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                color="primary"
                variant="contained"
                sx={{
                  borderRadius: "6px",
                  fontWeight: 600,
                  textTransform: "none",
                  px: 4,
                  boxShadow: "0 2px 8px rgba(25,118,210,0.25)",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(25,118,210,0.35)",
                  },
                }}
              >
                Confirm Selects ({selectedCount})
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  }
);

export const SelectsMadeDialog = React.memo(({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "8px",
          boxShadow: "0 12px 48px rgba(0,0,0,0.15)",
        }
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: "1.5rem", letterSpacing: "-0.02em" }}>
        Selects Already Made
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ fontSize: "1rem", lineHeight: 1.7 }}>
          It appears that your selects have already been confirmed. You can
          still browse your selects and download the storyboards.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          sx={{
            borderRadius: "6px",
            fontWeight: 600,
            textTransform: "none",
            px: 4,
            boxShadow: "0 2px 8px rgba(25,118,210,0.25)",
            "&:hover": {
              boxShadow: "0 4px 12px rgba(25,118,210,0.35)",
            },
          }}
        >
          Okay
        </Button>
      </DialogActions>
    </Dialog>
  );
});
